import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useWeb3 } from "@/hooks/useWeb3";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Menu, Bell, Coins, Wallet, Crown, MapPin, X } from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import Sidebar from "@/components/Sidebar";
import MapView from "@/components/MapView";
import PhotoCaptureModal from "@/components/PhotoCaptureModal";

export default function Home() {
  const { user } = useAuth();
  const { account, connect, disconnect, isConnecting, isConnected } = useWeb3();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);

  useEffect(() => {
    // Request geolocation with better error handling
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setUserLocation(location);
            console.log("Location obtained:", location);
          },
          (error) => {
            console.error("Geolocation error:", error.message, error.code);
            // Always provide fallback location so map can load
            const fallbackLocation = { lat: -28.0167, lng: 153.4000 }; // Gold Coast, Australia
            setUserLocation(fallbackLocation);
            console.log("Using fallback location:", fallbackLocation);
          },
          {
            enableHighAccuracy: false, // Use less accurate but faster positioning
            timeout: 8000,
            maximumAge: 300000 // 5 minutes
          }
        );
      } else {
        console.error("Geolocation not supported by browser");
        const fallbackLocation = { lat: -28.0167, lng: 153.4000 }; // Gold Coast, Australia
        setUserLocation(fallbackLocation);
      }
    };

    // Try to get location immediately
    getLocation();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-dark-base text-white">
      {/* Top Bar */}
      <header className="relative z-20 bg-dark-base/90 backdrop-blur-sm p-4 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            className="text-neon-green mr-3"
            data-testid="button-menu"
          >
            <Menu size={20} />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold" data-testid="text-username">{user?.username || 'Explorer'}</span>
              {user?.discoveries && user.discoveries > 5 && (
                <span className="px-2 py-0.5 text-xs bg-coral text-white rounded-full">🎨 Artist</span>
              )}
            </div>
            <div className="text-xs text-gray-400" data-testid="text-level">Level {user?.level || 1} • {user?.discoveries || 0} Discoveries</div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center bg-dark-elevated px-2 py-1 rounded-full">
            <span className="text-yellow-400 mr-1 text-xs">🪙</span>
            <span className="text-xs font-medium" data-testid="text-tokens">{user?.tokens || 0}</span>
          </div>
          
          {/* Web3 Wallet Button */}
          {isConnected ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="text-purple-400 border-purple-400 hover:bg-purple-400/20 px-2 text-xs"
              onClick={disconnect}
              data-testid="button-wallet-connected"
            >
              <Wallet size={12} className="mr-1" />
              {account?.slice(0, 4)}...
            </Button>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              className="text-purple-400 border-purple-400 hover:bg-purple-400/20 px-2 text-xs"
              onClick={connect}
              disabled={isConnecting}
              data-testid="button-connect-wallet"
            >
              <Wallet size={12} className="mr-1" />
              Connect
            </Button>
          )}

          {/* Pro Subscription Button */}
          <Link to="/subscribe">
            <Button 
              variant="outline" 
              size="sm" 
              className="text-red-500 border-red-500 hover:bg-red-500/20 px-2 text-xs"
              data-testid="button-pro-upgrade"
            >
              <Crown size={12} className="mr-1" />
              VIP
            </Button>
          </Link>

          {/* Sign Out Button */}
          <Button 
            variant="outline" 
            size="sm" 
            className="text-gray-400 border-gray-400 hover:bg-gray-400/20 px-2 text-xs"
            onClick={() => window.location.href = '/api/logout'}
            data-testid="button-signout"
          >
            Sign Out
          </Button>
        </div>
      </header>

      {/* Location Permission Card */}
      {!userLocation && (
        <Card className="absolute top-4 left-4 right-4 z-50 bg-dark-elevated border-gray-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MapPin className="text-neon-green" size={20} />
                <div>
                  <p className="text-white font-medium">Enable Location Access</p>
                  <p className="text-gray-400 text-sm">Get personalized street art discoveries near you</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-neon-green text-black hover:bg-neon-green/90"
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                      });
                      toast({
                        title: "Location Enabled",
                        description: "Now showing discoveries near your location"
                      });
                    },
                    () => {
                      setUserLocation({ lat: -28.0167, lng: 153.4000 });
                      toast({
                        title: "Using Default Location",
                        description: "Showing Gold Coast, Australia",
                        variant: "destructive"
                      });
                    }
                  );
                }}
                data-testid="button-enable-location"
              >
                Allow Location
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Map Container */}
      <div className="flex-1 relative"
           style={{ height: 'calc(100vh - 80px)' }}>
        
        <MapView 
          userLocation={userLocation}
          onPhotoCapture={() => setPhotoCaptureOpen(true)}
        />
      </div>

      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />

      {/* Photo Capture Modal */}
      <PhotoCaptureModal
        isOpen={photoCaptureOpen}
        onClose={() => setPhotoCaptureOpen(false)}
        userLocation={userLocation}
      />
    </div>
  );
}
